import { cached, tracked } from '@glimmer/tracking';
import { isDecoratorArgs, isPropertyDecoratorArgs } from 'decorator-setup';

import attachToStrings from 'rise-ui/utils/attach-to-strings.js';
import { or, readOnly } from 'rise-ui/utils/object/computed.js';

const classMixNamespace = Symbol('classMix');

function getClassMixNamespace(target) {
  return target[classMixNamespace] ??= {};
}

/**
 * Component property decorator to mix CSS class configs.
 * Property becomes instance of ClassMix.
 * @decorator
 * @see ClassMix
 */
export function classMix() {
  return isDecoratorArgs(...arguments) ? propertyDecorator(...arguments) : propertyDecorator;
  function propertyDecorator(target, propertyKey, descriptor) {
    if (!isPropertyDecoratorArgs(...arguments)) {
      throw new Error('@classMix should be applied to a property');
    }
    return {
      get() {
        return getClassMixNamespace(this).classMix ??= new ClassMix(this);
      },
      set() {
        throw new Error('@classMix property cannot be set');
      },
    };
  }
}

/**
 * ClassMix input args
 * @typedef ClassMixArgs
 * @property {{}} [args.classConfig] Configuration as object
 * @property {string} [args.classConfig.mix] Classes to mix
 * @property {string} [args.classConfig.prefix] Prefix added to mixed classes
 * @property {string} [args.classConfig.suffix] Suffix added to mixed classes
 * @property {string} [args.classConfig.modifiers] Modifiers added to mixed classes
 * @property {string} [args.classMix] Classes to mix
 * @property {string} [args.classPrefix] Prefix added to mixed classes
 * @property {string} [args.classSuffix] Suffix added to mixed classes
 * @property {string} [args.classMod] Modifiers added to mixed classes
 */
/**
 * ClassMix properties
 * @typedef ClassMixProperties
 * @property {string} mixClassPrefix Prefixes to add to mixed classes
 * @property {string} mixClassSuffix Suffixes to add to mixed classes
 * @property {Function} mixClassNames Mix given classes with attachments (not modifiers)
 * @property {Function} mixClassModifiers Combine given modifiers with mixed modifiers
 * @property {Function} mixClassModConfig Combine given classes and modifiers with mixed classes and modifiers as `class-mod` helper config
 */
/**
 * Mixes and modifies component classes with inputs
 * @typedef ClassMix
 * @implements ClassMixProperties
 * @property {ClassMixArgs} context
 */
export class ClassMix {
  @tracked context;

  constructor(context) {
    this.context = context;
  }

  // For some reason @readOnly/get isn't reactive on args and gets disconnected
  get args() {
    return this.context.args;
  }

  @readOnly('args.classConfig') mixedClassConfig;
  @or('args.{classMix,mixedClassConfig.mix}') mixedClassNames;
  @or('args.{classPrefix,mixedClassConfig.prefix}') mixedClassPrefix;
  @or('args.{classSuffix,mixedClassConfig.suffix}') mixedClassSuffix;
  @or('args.{classMod,mixedClassConfig.modifiers}') mixedClassModifiers;

  /**
   * Gives array of class names from a given string
   * @param {string} classNames Class string to parse
   * @returns {string[]}
   */
  parseClassNames(classNames) {
    return typeof classNames === 'string' ? classNames.trim().split(/\s+/)
      : classNames || [];
  }

  @cached get mixClassPrefix() {
    return [].concat(this.parseClassNames(this.mixedClassPrefix)).join(' ');
  }

  @cached get mixClassSuffix() {
    return [].concat(this.parseClassNames(this.mixedClassSuffix)).join(' ');
  }

  @cached get mixClassNames() {
    const { mixClassPrefix, mixedClassNames, mixClassSuffix } = this; // Consume
    const options = {
      prefix: mixClassPrefix,
      suffix: mixClassSuffix,
    };
    return classNames => {
      const parseClassNames = this.parseClassNames;
      const mixedClasses = [].concat(
        parseClassNames(classNames),
        parseClassNames(mixedClassNames),
      );
      return attachToStrings(mixedClasses, options).join(' ');
    };
  }

  @cached get mixClassModifiers() {
    const { mixedClassModifiers } = this; // Consume
    return (...classModArgs) => [
      ...(mixedClassModifiers || []),
      ...classModArgs,
    ];
  }

  @cached get mixClassModConfig() {
    const { mixClassNames, mixClassModifiers } = this; // Consume
    return (classNames, ...classModArgs) => ({
      classNames: this.mixClassNames(classNames),
      modifiers: this.mixClassModifiers(...classModArgs),
    });
  }
}
